import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { FolderDriveItem } from '../../../../components/Document/DocumentFlatTree'
import { Button, Input, makeStyles, Popover, PopoverSurface, PopoverTrigger, shorthands, tokens } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { InputProps } from '@fluentui/react-input'
import useEntity from '../../../../hooks/useEntity'
import { Destination, GraphError, IGraphArrayPayload } from '../../../../context/AxiosContext'
import { SearchRequest, SearchResponse } from '@microsoft/microsoft-graph-types'
import { bundleIcon, DismissSquareFilled, SearchRegular } from '@fluentui/react-icons'
import LoadingIcon from '../../../../components/Icon/LoadingIcon'
import { usePrevious } from '@fluentui/react-hooks'
import { SearchHintIcon } from '../../../../pages/BaseOverview/BaseOverviewFilter'

const DismissSquareIcon = bundleIcon(DismissSquareFilled, DismissSquareFilled)

const useStyles = makeStyles({
    inverted: {
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundInverted,
    },
    colorRed: {
        color: tokens.colorStatusDangerForeground3,
    },
    filterInput: {
        ...shorthands.padding(0, tokens.spacingHorizontalXS),
    },
    flexRow: {
        display: 'flex',
        flexDirection: 'row',
    },
})

type FileDocumentSearchBoxProps = {
    rootFolder: FolderDriveItem
    driveItems: FolderDriveItem[]
    setFilteredDriveItems: (items: FolderDriveItem[]) => void
    reset?: () => void

    componentProps?: InputProps
    placeholderKey?: string
}

export type SearchRequestWrapper = { requests: SearchRequest[] }

/**
 * Compose a Search Query request body using the following documentation:
 * https://learn.microsoft.com/en-us/graph/search-concept-files#example-5-use-filters-in-search-queries
 *
 * @param query The search terms.
 * @param fileRootUrl The URL of the File root folder.
 */
const getSearchRequest = (query: string, fileRootUrl: string): SearchRequestWrapper => ({
    requests: [
        {
            entityTypes: ['driveItem'],
            query: {
                queryString: `${query} AND path:"${fileRootUrl}"`,
            },
        },
    ],
})

function FilterInputHint() {
    const { t: translate } = useTranslation()

    const [open, setOpen] = useState<boolean>(false)

    return (
        <Popover open={open} onOpenChange={(e, data) => setOpen(data.open)} withArrow>
            <PopoverTrigger disableButtonEnhancement>
                <Button size={'small'} appearance={'transparent'} icon={<SearchHintIcon />} />
            </PopoverTrigger>
            <PopoverSurface tabIndex={-1}>
                <h3>{translate('file.pivot.document.search.hint.title')}</h3>
                <br />
                <div dangerouslySetInnerHTML={{ __html: translate('file.pivot.document.search.hint.body') }}></div>
            </PopoverSurface>
        </Popover>
    )
}

export default function FileDocumentSearchBox(props: FileDocumentSearchBoxProps): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const [query, setQuery] = useState<string>('')
    const prevQuery = usePrevious(query)
    const [submittedQuery, setSubmittedQuery] = useState<string>(undefined)
    const [submitting, setSubmitting] = useState<boolean>(false)

    const searchRequest = useMemo<SearchRequestWrapper>(
        () => (submittedQuery && props.rootFolder?.webDavUrl ? getSearchRequest(query, props.rootFolder?.webDavUrl) : null),
        [submittedQuery, props.rootFolder?.webDavUrl],
    )

    const [searchResults] = useEntity<string[], IGraphArrayPayload<SearchResponse>, GraphError, SearchRequestWrapper>(
        data => ({ method: 'POST', url: '/search/query', data, baseURL: Destination.SharePoint }),
        searchRequest,
        result =>
            result.data.value.flatMap((value: SearchResponse) =>
                value.hitsContainers.flatMap(hitsContainer => hitsContainer?.hits?.map(hit => hit.hitId) || []),
            ),
    )

    useEffect(() => {
        setSubmitting(false)
        if (searchResults.loaded) {
            props.setFilteredDriveItems(
                props.driveItems
                    .filter(item => searchResults.data?.includes(item.id))
                    .filter(item => item.itemType === 'leaf')
                    .map(item => ({ ...item, parentValue: undefined })),
            )

            setSubmittedQuery(undefined)
            setSubmitting(false)
        } else if (searchResults.loading) {
            setSubmitting(true)
        }
    }, [searchResults])

    useEffect(() => {
        if (query?.length === 0 && prevQuery?.length > 0) {
            setSubmittedQuery(undefined)
            setSubmitting(false)
            props.reset?.()
        }
    }, [query])

    return (
        <Input
            className={styles.filterInput}
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            {...props}
            placeholder={props.placeholderKey && translate(props.placeholderKey)}
            appearance={'underline'}
            disabled={submitting}
            value={query}
            contentBefore={
                <div className={styles.flexRow}>
                    <FilterInputHint />
                    <Button size={'small'} appearance={'transparent'} icon={submitting ? <LoadingIcon /> : <SearchRegular />} />
                </div>
            }
            contentAfter={
                query.length > 0 && (
                    <Button
                        size={'small'}
                        appearance={'transparent'}
                        className={styles.colorRed}
                        icon={<DismissSquareIcon />}
                        onClick={() => {
                            setQuery('')
                        }}
                    />
                )
            }
            onKeyDown={e => {
                e.stopPropagation()
                if (e.key === 'Enter') {
                    setSubmittedQuery(query)
                } else if (e.key === 'Esc') {
                    setQuery('')
                }
            }}
            onChange={(e, data) => setQuery(data.value)}
        />
    )
}
